import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface DevizaMateriale {
  id: string;
  numar: string;
  data: string;
  furnizor: string;
  materiale: Array<{
    nume: string;
    cantitate: number;
    unitate: string;
    pretUnitar: number;
    total: number;
  }>;
  total: number;
  parohieId?: string;
  status: 'in_asteptare' | 'aprobata' | 'anulata';
  observatii?: string;
}

const DevizeMateriale: React.FC = () => {
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterFurnizor, setFilterFurnizor] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDeviza, setSelectedDeviza] = useState<DevizaMateriale | null>(null);

  // Mock data pentru devize materiale
  const [devizeMateriale] = useState<DevizaMateriale[]>([
    {
      id: 'dm1',
      numar: 'DM-001/2024',
      data: '2024-01-18',
      furnizor: 'SC Materiale Bisericesti SRL',
      materiale: [
        { nume: 'Candelabru de argint', cantitate: 2, unitate: 'buc', pretUnitar: 500, total: 1000 },
        { nume: 'Cărți religioase', cantitate: 50, unitate: 'buc', pretUnitar: 25, total: 1250 },
        { nume: 'Mobilier bisericesc', cantitate: 1, unitate: 'set', pretUnitar: 2000, total: 2000 }
      ],
      total: 4250,
      parohieId: 'parohia1',
      status: 'in_asteptare',
      observatii: 'Pentru renovarea bisericii'
    }
  ]);

  const furnizori = Array.from(new Set(devizeMateriale.map(d => d.furnizor)));

  const filteredDevize = devizeMateriale.filter(deviza => {
    const matchesSearch = deviza.numar.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         deviza.furnizor.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || deviza.status === filterStatus;
    const matchesFurnizor = !filterFurnizor || deviza.furnizor === filterFurnizor;
    
    return matchesSearch && matchesStatus && matchesFurnizor;
  });

  const totalInAsteptare = devizeMateriale
    .filter(d => d.status === 'in_asteptare')
    .reduce((sum, d) => sum + d.total, 0);

  const totalAprobate = devizeMateriale
    .filter(d => d.status === 'aprobata')
    .reduce((sum, d) => sum + d.total, 0);

  const totalGeneral = devizeMateriale.reduce((sum, d) => sum + d.total, 0);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'aprobata': return 'success';
      case 'in_asteptare': return 'warning';
      case 'anulata': return 'error';
      default: return 'default';
    }
  };

  const handleViewDeviza = (deviza: DevizaMateriale) => {
    setSelectedDeviza(deviza);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDeviza(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Devize Materiale
        </Typography>
        {hasPermission('gestiune.write') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {/* Implementare adăugare deviză nouă */}}
          >
            Deviză nouă
          </Button>
        )}
      </Box>

      {/* Statistici devize */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography color="textSecondary" sx={{ mr: 1 }}>
                  ⏳
                </Typography>
                <Typography color="textSecondary">
                  În așteptare
                </Typography>
              </Box>
              <Typography variant="h5" color="warning.main">
                €{totalInAsteptare.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography color="textSecondary" sx={{ mr: 1 }}>
                  ✅
                </Typography>
                <Typography color="textSecondary">
                  Aprobate
                </Typography>
              </Box>
              <Typography variant="h5" color="success.main">
                €{totalAprobate.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography color="textSecondary" sx={{ mr: 1 }}>
                  💰
                </Typography>
                <Typography color="textSecondary">
                  Total general
                </Typography>
              </Box>
              <Typography variant="h5" color="primary.main">
                €{totalGeneral.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtre pentru devize */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Caută devize..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="">Toate</MenuItem>
                <MenuItem value="in_asteptare">În așteptare</MenuItem>
                <MenuItem value="aprobata">Aprobată</MenuItem>
                <MenuItem value="anulata">Anulată</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Furnizor</InputLabel>
              <Select
                value={filterFurnizor}
                onChange={(e) => setFilterFurnizor(e.target.value)}
                label="Furnizor"
              >
                <MenuItem value="">Toți</MenuItem>
                {furnizori.map(furnizor => (
                  <MenuItem key={furnizor} value={furnizor}>{furnizor}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('');
                setFilterFurnizor('');
              }}
            >
              Resetează
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabel devize */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Număr</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Furnizor</TableCell>
              <TableCell>Materiale</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Acțiuni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDevize.map((deviza) => (
              <TableRow key={deviza.id}>
                <TableCell>{deviza.numar}</TableCell>
                <TableCell>{deviza.data}</TableCell>
                <TableCell>{deviza.furnizor}</TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {deviza.materiale.length} materiale
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {deviza.materiale.map(m => m.nume).join(', ')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography fontWeight="bold">
                    €{deviza.total.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={deviza.status}
                    color={getStatusColor(deviza.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => handleViewDeviza(deviza)}
                    color="primary"
                  >
                    <ViewIcon />
                  </IconButton>
                  {hasPermission('gestiune.write') && (
                    <IconButton color="secondary">
                      <EditIcon />
                    </IconButton>
                  )}
                  <IconButton color="default">
                    <DownloadIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog pentru vizualizare deviză */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalii deviză materiale
        </DialogTitle>
        <DialogContent>
          {selectedDeviza && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Număr deviză
                </Typography>
                <Typography variant="body1">
                  {selectedDeviza.numar}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Data
                </Typography>
                <Typography variant="body1">
                  {selectedDeviza.data}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Furnizor
                </Typography>
                <Typography variant="body1">
                  {selectedDeviza.furnizor}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                <Chip
                  label={selectedDeviza.status}
                  color={getStatusColor(selectedDeviza.status) as any}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Materiale
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nume</TableCell>
                      <TableCell>Cantitate</TableCell>
                      <TableCell>Preț unitar</TableCell>
                      <TableCell>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedDeviza.materiale.map((material, index) => (
                      <TableRow key={index}>
                        <TableCell>{material.nume}</TableCell>
                        <TableCell>{material.cantitate} {material.unitate}</TableCell>
                        <TableCell>€{material.pretUnitar.toLocaleString()}</TableCell>
                        <TableCell>€{material.total.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Total deviză
                </Typography>
                <Typography variant="h5" color="primary.main">
                  €{selectedDeviza.total.toLocaleString()}
                </Typography>
              </Grid>
              {selectedDeviza.observatii && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Observații
                  </Typography>
                  <Typography variant="body1">
                    {selectedDeviza.observatii}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Închide</Button>
          <Button variant="contained" startIcon={<DownloadIcon />}>
            Descarcă
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DevizeMateriale;
